import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { ALL_PAGES } from '../components/Layout';
import { useLab } from '../i18n/LabContext';
import { useLang } from '../i18n/LanguageContext';

const STATUS_BADGE = {
  trial:     { color: 'var(--yellow)', tKey: 'superStatusTrial' },
  active:    { color: 'var(--green)',  tKey: 'superStatusActive' },
  expired:   { color: 'var(--red)',    tKey: 'superStatusExpired' },
  cancelled: { color: 'var(--text3)', tKey: 'superStatusCancelled' },
};

function fmtN(n) { return Number(n || 0).toLocaleString('uz-UZ'); }

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function daysLeft(ts) {
  if (!ts) return null;
  return Math.ceil((new Date(ts) - Date.now()) / 86400000);
}

export default function SuperAdmin() {
  const [tab, setTab]         = useState('select');
  const [owners, setOwners]   = useState([]);
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState({ plan_id: 1, months: 1, notes: '' });
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);

  const [analytics, setAnalytics]   = useState(null);
  const [period, setPeriod]         = useState('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo]     = useState('');
  const [aLoading, setALoading]     = useState(false);
  const [detail, setDetail]         = useState(null);
  const [dLoading, setDLoading]     = useState(false);
  const [expCategory, setExpCategory] = useState(null);

  const { t } = useLang();
  const fmt = n => `${Number(n || 0).toLocaleString('uz-UZ')} ${t('currency')}`;

  async function openDetail(type, lab_id, lab_name) {
    setDLoading(true);
    setExpCategory(null);
    setDetail({ type, lab_id, lab_name, data: null });
    try {
      let url = `/subscriptions/admin/analytics/${type}?period=${period}`;
      if (lab_id) url += `&lab_id=${lab_id}`;
      if (period === 'custom' && customFrom && customTo) url += `&from=${customFrom}&to=${customTo}`;
      const r = await api.get(url);
      setDetail({ type, lab_id, lab_name, data: r.data });
    } catch(e) {
      setDetail({ type, lab_id, lab_name, data: { error: e.message } });
    }
    setDLoading(false);
  }

  const loadAnalytics = useCallback(async (p = period, cf = customFrom, ct = customTo) => {
    setALoading(true);
    try {
      let url = `/subscriptions/admin/analytics?period=${p}`;
      if (p === 'custom' && cf && ct) url += `&from=${cf}&to=${ct}`;
      const r = await api.get(url);
      setAnalytics(r.data);
    } catch {}
    setALoading(false);
  }, [period, customFrom, customTo]);

  async function load() {
    try {
      const [o, p] = await Promise.all([
        api.get('/subscriptions/admin/all').then(r => r.data),
        api.get('/subscriptions/plans').then(r => r.data),
      ]);
      setOwners(o);
      setPlans(p);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || t('superErrorLoading') });
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadAnalytics(); }, []);

  async function setSubscription() {
    if (!modal) return;
    setSaving(true);
    try {
      await api.post(`/subscriptions/admin/${modal.ownerId}/set`, form);
      setMsg({ type: 'success', text: t('superMsgSubSet').replace('{name}', modal.ownerName) });
      setModal(null);
      load();
    } catch (e) {
      setMsg({ type: 'error', text: `❌ ${e.response?.data?.error || t('error')}` });
    } finally {
      setSaving(false);
    }
  }

  async function cancelSubscription(ownerId, ownerName) {
    if (!window.confirm(t('superConfirmCancel').replace('{name}', ownerName))) return;
    try {
      await api.post(`/subscriptions/admin/${ownerId}/cancel`);
      setMsg({ type: 'success', text: t('superMsgSubCancelled').replace('{name}', ownerName) });
      load();
    } catch (e) {
      setMsg({ type: 'error', text: `❌ ${t('error')}` });
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--cyan)', fontSize: 18 }}>{t('loading')}</div>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>👑 Super Admin</h1>
          <div style={{ color: 'var(--text2)', fontSize: 13 }}>{t('superAdminSubtitle')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['select', t('superTabSelect')], ['analytics', t('superTabAnalytics')], ['owners', t('superTabOwners')], ['menu', t('superTabMenu')]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{
              padding: '9px 20px', borderRadius: 10, border: '1px solid',
              borderColor: tab === k ? 'var(--cyan)' : 'var(--border)',
              background: tab === k ? 'rgba(0,212,255,0.08)' : 'transparent',
              color: tab === k ? 'var(--cyan)' : 'var(--text2)',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            }}>{l}</button>
          ))}
        </div>
      </div>

      {msg && (
        <div style={{
          padding: '12px 18px', borderRadius: 10, marginBottom: 20,
          background: msg.type === 'success' ? 'rgba(0,255,136,0.1)' : 'rgba(255,59,92,0.1)',
          border: `1px solid ${msg.type === 'success' ? 'var(--green)' : 'var(--red)'}`,
          color: msg.type === 'success' ? 'var(--green)' : 'var(--red)', fontSize: 13,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>{msg.text}</span>
          <span style={{ cursor: 'pointer', opacity: 0.6 }} onClick={() => setMsg(null)}>✕</span>
        </div>
      )}

      {/* ======= ANALITIKA TAB ======= */}
      {tab === 'analytics' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 24, alignItems: 'center', flexWrap: 'wrap' }}>
            {[['today', t('superPeriodToday')], ['week', t('superPeriodWeek')], ['month', t('superPeriodMonth')], ['year', t('superPeriodYear')], ['custom', t('superPeriodCustom')]].map(([v, l]) => (
              <button key={v} onClick={() => { setPeriod(v); if (v !== 'custom') loadAnalytics(v); }}
                style={{ padding: '8px 18px', borderRadius: 20, border: '1px solid',
                  borderColor: period === v ? 'var(--purple)' : 'var(--border)',
                  background: period === v ? 'rgba(124,58,237,0.1)' : 'transparent',
                  color: period === v ? 'var(--purple)' : 'var(--text2)',
                  cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
                {l}
              </button>
            ))}
            {period === 'custom' && (
              <>
                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                  style={{ background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 10, fontSize: 13 }} />
                <span style={{ color: 'var(--text3)' }}>—</span>
                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                  style={{ background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 10, fontSize: 13 }} />
                <button onClick={() => loadAnalytics('custom', customFrom, customTo)}
                  style={{ padding: '8px 18px', borderRadius: 10, border: 'none', background: 'var(--purple)', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
                  {t('superBtnView')}
                </button>
              </>
            )}
            {aLoading && <span style={{ color: 'var(--text3)', fontSize: 13 }}>⏳ {t('loading')}</span>}
          </div>

          {analytics && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
                {[
                  { icon: '💰', label: t('superTotalIncome'),    val: fmt(analytics.totals.total_income),   color: 'var(--cyan)' },
                  { icon: '🎮', label: t('superSessionIncome'),  val: fmt(analytics.totals.session_income), color: 'var(--purple)' },
                  { icon: '💸', label: t('superTotalExpenses'),  val: fmt(analytics.totals.expenses),       color: 'var(--red)' },
                  { icon: '📈', label: t('superNetProfit'),      val: fmt(analytics.totals.profit),         color: analytics.totals.profit >= 0 ? 'var(--green)' : 'var(--red)' },
                ].map(c => (
                  <div key={c.label} style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 20px' }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>{c.icon}</div>
                    <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 18, fontWeight: 700, color: c.color, marginBottom: 6 }}>{c.val}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 1 }}>{c.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{t('superByBranchAnalysis')}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                    {new Date(analytics.from).toLocaleDateString('uz-UZ')} — {new Date(analytics.to).toLocaleDateString('uz-UZ')}
                  </div>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--card)' }}>
                      {[t('superThBranchName'), 'PC', t('superThCash'), t('superThTerminal'), t('superThClick'), t('superThPayme'), t('superThRevenue'), t('superTotalExpenses'), t('superNetProfit')].map(h => (
                        <th key={h} style={{ padding: '11px 16px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.owners.map((o, i) => (
                      <tr key={o.lab_id} style={{ borderBottom: i < analytics.owners.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <td style={{ padding: '13px 16px', fontWeight: 600 }}>
                          <div>{o.lab_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{o.lab_address || o.owner_name}</div>
                        </td>
                        <td style={{ padding: '13px 16px', color: 'var(--text2)' }}>{o.computer_count} {t('superUnitPcs')}</td>
                        <td style={{ padding: '13px 16px', color: 'var(--green)' }}>{fmtN(o.naqd)}</td>
                        <td style={{ padding: '13px 16px', color: 'var(--cyan)' }}>{fmtN(o.terminal)}</td>
                        <td style={{ padding: '13px 16px', color: 'var(--purple)' }}>{fmtN(o.click)}</td>
                        <td style={{ padding: '13px 16px', color: 'var(--yellow)' }}>{fmtN(o.payme)}</td>
                        <td style={{ padding: '13px 16px', fontWeight: 700 }}>{fmtN(o.jami)}</td>
                        <td style={{ padding: '13px 16px', color: 'var(--red)' }}>{fmtN(o.expenses)}</td>
                        <td style={{ padding: '13px 16px', fontWeight: 700, color: o.profit >= 0 ? 'var(--green)' : 'var(--red)' }}>
                          {o.profit >= 0 ? '+' : ''}{fmtN(o.profit)}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: 'rgba(0,212,255,0.04)', borderTop: '2px solid var(--border)' }}>
                      <td colSpan={2} style={{ padding: '13px 16px', fontWeight: 800, color: 'var(--cyan)' }}>{t('superTotalRow')}</td>
                      <td style={{ padding: '13px 16px', fontWeight: 700, color: 'var(--green)' }}>{fmtN(analytics.totals.naqd)}</td>
                      <td style={{ padding: '13px 16px', fontWeight: 700, color: 'var(--cyan)' }}>{fmtN(analytics.totals.terminal)}</td>
                      <td style={{ padding: '13px 16px', fontWeight: 700, color: 'var(--purple)' }}>{fmtN(analytics.totals.click)}</td>
                      <td style={{ padding: '13px 16px', fontWeight: 700, color: 'var(--yellow)' }}>{fmtN(analytics.totals.payme)}</td>
                      <td style={{ padding: '13px 16px', fontWeight: 800 }}>{fmtN(analytics.totals.jami)}</td>
                      <td style={{ padding: '13px 16px', fontWeight: 700, color: 'var(--red)' }}>{fmtN(analytics.totals.expenses)}</td>
                      <td style={{ padding: '13px 16px', fontWeight: 800, color: analytics.totals.profit >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        {analytics.totals.profit >= 0 ? '+' : ''}{fmtN(analytics.totals.profit)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 🍔 BAR / SNACK BO'LIMI */}
              {analytics.bar && (
                <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', marginTop: 24 }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>🍔 Bar / Snack</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                      Sof foyda = tovar sotish narxi − tannarxi (agar mahsulotda tannarx 0 bo'lsa, sof foyda = jami savdoga teng)
                    </div>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--card)' }}>
                        {[t('superThBranchName'), 'Jami savdo', 'Tovar tannarxi', 'Sof foyda'].map(h => (
                          <th key={h} style={{ padding: '11px 16px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.bar.map((b, i) => (
                        <tr key={b.lab_id} style={{ borderBottom: i < analytics.bar.length - 1 ? '1px solid var(--border)' : 'none' }}>
                          <td style={{ padding: '13px 16px', fontWeight: 600 }}>
                            <div>{b.lab_name}</div>
                            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{b.lab_address || ''}</div>
                          </td>
                          <td style={{ padding: '13px 16px', fontWeight: 700, color: 'var(--purple)' }}>{fmtN(b.bar_sales)}</td>
                          <td style={{ padding: '13px 16px', color: 'var(--text2)' }}>{fmtN(b.bar_cost)}</td>
                          <td style={{ padding: '13px 16px', fontWeight: 700, color: b.bar_profit >= 0 ? 'var(--green)' : 'var(--red)' }}>
                            {b.bar_profit >= 0 ? '+' : ''}{fmtN(b.bar_profit)}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ background: 'rgba(124,58,237,0.05)', borderTop: '2px solid var(--border)' }}>
                        <td style={{ padding: '13px 16px', fontWeight: 800, color: 'var(--purple)' }}>{t('superTotalRow')}</td>
                        <td style={{ padding: '13px 16px', fontWeight: 800, color: 'var(--purple)' }}>{fmtN(analytics.bar_totals.bar_sales)}</td>
                        <td style={{ padding: '13px 16px', fontWeight: 700 }}>{fmtN(analytics.bar_totals.bar_cost)}</td>
                        <td style={{ padding: '13px 16px', fontWeight: 800, color: analytics.bar_totals.bar_profit >= 0 ? 'var(--green)' : 'var(--red)' }}>
                          {analytics.bar_totals.bar_profit >= 0 ? '+' : ''}{fmtN(analytics.bar_totals.bar_profit)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ======= FILIALLAR TAB ======= */}
      {tab === 'owners' && (
      <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
        {[
          { icon: '👥', val: owners.length,                                                   label: t('superStatTotalOwners'),         color: 'var(--cyan)' },
          { icon: '✅', val: owners.filter(o => o.status === 'active').length,                label: t('superStatActiveSubscriptions'), color: 'var(--green)' },
          { icon: '🧪', val: owners.filter(o => o.status === 'trial').length,                 label: t('superStatTrialPeriod'),         color: 'var(--yellow)' },
          { icon: '🖥️', val: owners.reduce((s, o) => s + parseInt(o.computer_count || 0), 0), label: t('superStatTotalComputers'),      color: 'var(--purple)' },
        ].map(c => (
          <div key={c.label} style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px' }}>
            <div style={{ fontSize: 22 }}>{c.icon}</div>
            <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 20, fontWeight: 700, color: c.color, margin: '8px 0 4px' }}>{c.val}</div>
            <div style={{ fontSize: 11, color: 'var(--text2)' }}>{c.label}</div>
          </div>
        ))}
      </div>

      <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--card)' }}>
              {[t('superThOwner'), 'Email', t('superThPlan'), t('superThStatus'), 'PC', t('superThExpires'), t('superThActions')].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {owners.map(o => {
              const statusMeta = STATUS_BADGE[o.status] || STATUS_BADGE.trial;
              const expiry = o.status === 'active' ? o.current_period_end : o.status === 'trial' ? o.trial_ends_at : null;
              const dl = daysLeft(expiry);

              return (
                <tr key={o.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600 }}>{o.name}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--text2)', fontSize: 12 }}>{o.email}</td>
                  <td style={{ padding: '12px 16px' }}>
                    {o.plan_name ? (
                      <span style={{ color: 'var(--cyan)', fontWeight: 600 }}>{o.plan_name}</span>
                    ) : (
                      <span style={{ color: 'var(--text3)' }}>{t('superNoPlan')}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      background: statusMeta.color + '18', border: `1px solid ${statusMeta.color}50`,
                      color: statusMeta.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    }}>{t(statusMeta.tKey)}</span>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--text)', fontWeight: 600 }}>
                    {o.computer_count || 0}
                    {o.max_computers && <span style={{ color: 'var(--text3)', fontWeight: 400 }}>/{o.max_computers}</span>}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12 }}>
                    {expiry ? (
                      <span style={{ color: dl !== null && dl < 7 ? 'var(--red)' : 'var(--text2)' }}>
                        {fmtDate(expiry)}
                        {dl !== null && <span style={{ marginLeft: 6, fontSize: 10 }}>({dl}{t('superDayAbbr')})</span>}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => { setModal({ ownerId: o.id, ownerName: o.name }); setForm({ plan_id: plans[0]?.id || 1, months: 1, notes: '' }); }}
                        style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid var(--cyan)', color: 'var(--cyan)', padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 11 }}
                      >{t('superBtnAssign')}</button>
                      {o.status === 'active' && (
                        <button
                          onClick={() => cancelSubscription(o.id, o.name)}
                          style={{ background: 'rgba(255,59,92,0.08)', border: '1px solid var(--red)', color: 'var(--red)', padding: '5px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 11 }}
                        >{t('cancelShort')}</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      </div>
      )}

      {/* ======= FILIALNI TANLASH TAB ======= */}
      {tab === 'select' && <SelectLab onSelected={(m) => setMsg(m)} />}

      {/* ======= MENYU TARTIBI TAB ======= */}
      {tab === 'menu' && <MenuOrder onSaved={(m) => setMsg(m)} />}


      {/* MODAL — obuna belgilash */}
      {modal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)',
        }}>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 20, padding: '32px 36px', width: 420 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{t('superModalSubTitle')}</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 24 }}>
              {t('superModalSubFor').replace('{name}', modal.ownerName)}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: 'var(--text2)', letterSpacing: 1, display: 'block', marginBottom: 8 }}>{t('superLabelPlan')}</label>
              <select
                value={form.plan_id}
                onChange={e => setForm(f => ({ ...f, plan_id: parseInt(e.target.value) }))}
                style={{ width: '100%', background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 14px', borderRadius: 10, fontSize: 14, outline: 'none' }}
              >
                {plans.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {Number(p.price_monthly).toLocaleString()} {t('currency')}/{t('superMonthAbbr')} ({p.max_computers >= 999 ? t('superUnlimited') : p.max_computers + ' PC'})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: 'var(--text2)', letterSpacing: 1, display: 'block', marginBottom: 8 }}>{t('superLabelDuration')}</label>
              <select
                value={form.months}
                onChange={e => setForm(f => ({ ...f, months: parseInt(e.target.value) }))}
                style={{ width: '100%', background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 14px', borderRadius: 10, fontSize: 14, outline: 'none' }}
              >
                {[1, 3, 6, 12].map(m => <option key={m} value={m}>{m} {t('superMonthAbbr')}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 11, color: 'var(--text2)', letterSpacing: 1, display: 'block', marginBottom: 8 }}>{t('superLabelNote')}</label>
              <input
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder={t('superPlaceholderOptional')}
                style={{ width: '100%', background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 14px', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setModal(null)} style={{ flex: 1, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', padding: '12px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontFamily: 'Inter, sans-serif' }}>
                {t('cancelShort')}
              </button>
              <button onClick={setSubscription} disabled={saving} style={{ flex: 1, background: 'linear-gradient(135deg, var(--purple), #5b21b6)', border: 'none', color: 'white', padding: '12px', borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
                {saving ? `⏳ ${t('savingLabel')}` : `✅ ${t('superBtnAssign')}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── FILIALNI TANLASH ──
function SelectLab({ onSelected }) {
  const { labs, labId, setLabId, loading } = useLab();
  const { t } = useLang();

  if (loading) return <div style={{ color: 'var(--text3)' }}>{t('loading')}</div>;
  if (!labs.length) return <div style={{ color: 'var(--text3)' }}>{t('superNoLabsFound')}</div>;

  const pick = (lab) => {
    setLabId(lab.id);
    onSelected?.({ type: 'success', text: t('superMsgActiveBranch').replace('{name}', lab.name) });
  };

  return (
    <div>
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{t('superSelectLabTitle')}</div>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>{t('superSelectLabHint')}</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {labs.map(lab => {
          const active = lab.id === labId;
          return (
            <div
              key={lab.id}
              onClick={() => pick(lab)}
              style={{
                cursor: 'pointer', padding: '16px 18px', borderRadius: 12,
                background: active ? 'rgba(0,212,255,0.1)' : 'var(--card2)',
                border: `1.5px solid ${active ? 'var(--cyan)' : 'var(--border)'}`,
                boxShadow: active ? '0 0 18px -4px rgba(0,212,255,0.4)' : 'none',
                transition: 'all .15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{lab.name}</div>
                {active && <span style={{ fontSize: 11, padding: '2px 8px', background: 'var(--cyan)', color: '#000', borderRadius: 6, fontWeight: 700 }}>{t('superBadgeActive')}</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>{lab.address || '—'}</div>
              <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 11, color: 'var(--text3)' }}>
                <span>🖥 {lab.total_computers || 0} PC</span>
                <span>● {lab.available_computers || 0} {t('superAvailableSuffix')}</span>
                <span>💵 {Number(lab.price_per_hour || 0).toLocaleString()} {t('currency')}/{t('superPerHour')}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── MENYU TARTIBI ──
function MenuOrder({ onSaved }) {
  const { t } = useLang();

  const initial = () => {
    try {
      const saved = JSON.parse(localStorage.getItem('menu_order') || '[]');
      if (Array.isArray(saved) && saved.length) {
        const set = new Set(saved);
        const ordered = saved.filter(s => ALL_PAGES.some(p => p.slug === s));
        for (const p of ALL_PAGES) if (!set.has(p.slug)) ordered.push(p.slug);
        return ordered;
      }
    } catch {}
    return ALL_PAGES.map(p => p.slug);
  };
  const [order, setOrder] = useState(initial);
  const [dragIdx, setDragIdx] = useState(null);

  const move = (from, to) => {
    if (to < 0 || to >= order.length) return;
    const next = [...order];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    setOrder(next);
  };

  const save = () => {
    localStorage.setItem('menu_order', JSON.stringify(order));
    onSaved?.({ type: 'success', text: t('superMsgMenuSaved') });
  };
  const reset = () => {
    localStorage.removeItem('menu_order');
    setOrder(ALL_PAGES.map(p => p.slug));
    onSaved?.({ type: 'success', text: t('superMsgMenuReset') });
  };

  const pageBy = Object.fromEntries(ALL_PAGES.map(p => [p.slug, p]));

  return (
    <div>
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{t('superMenuOrderTitle')}</div>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>{t('superMenuOrderHint')}</div>
      </div>

      <div style={{ display: 'grid', gap: 6, maxWidth: 560 }}>
        {order.map((slug, i) => {
          const p = pageBy[slug];
          if (!p) return null;
          const dragging = dragIdx === i;
          return (
            <div
              key={slug}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={e => {
                e.preventDefault();
                if (dragIdx != null && dragIdx !== i) {
                  move(dragIdx, i);
                  setDragIdx(i);
                }
              }}
              onDragEnd={() => setDragIdx(null)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderRadius: 10,
                background: dragging ? 'rgba(0,212,255,0.12)' : 'var(--card2)',
                border: `1px solid ${dragging ? 'var(--cyan)' : 'var(--border)'}`,
                cursor: 'grab', userSelect: 'none',
              }}
            >
              <span style={{ color: 'var(--text3)', fontSize: 14 }}>⋮⋮</span>
              <span style={{ fontSize: 16 }}>{p.icon}</span>
              <span style={{ flex: 1, fontSize: 13, color: 'var(--text)' }}>{p.label}</span>
              <span style={{ fontSize: 10, color: 'var(--text3)', minWidth: 26, textAlign: 'right' }}>{i + 1}</span>
              <button onClick={() => move(i, i - 1)} disabled={i === 0} style={{
                width: 28, height: 28, padding: 0, borderRadius: 6, cursor: 'pointer',
                background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)',
                opacity: i === 0 ? 0.3 : 1,
              }}>↑</button>
              <button onClick={() => move(i, i + 1)} disabled={i === order.length - 1} style={{
                width: 28, height: 28, padding: 0, borderRadius: 6, cursor: 'pointer',
                background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)',
                opacity: i === order.length - 1 ? 0.3 : 1,
              }}>↓</button>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        <button onClick={save} style={{
          background: 'linear-gradient(135deg, var(--cyan), var(--purple))', border: 'none',
          color: '#fff', padding: '11px 24px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>{t('superBtnSave')}</button>
        <button onClick={reset} style={{
          background: 'transparent', border: '1px solid var(--border)',
          color: 'var(--text2)', padding: '11px 18px', borderRadius: 10, fontSize: 13, cursor: 'pointer',
        }}>{t('superBtnResetOrder')}</button>
      </div>
    </div>
  );
}
